import { jsx as _jsx } from "react/jsx-runtime";
import { useSchools } from '@src/hooks/useSchools';
import { debounce } from 'lodash';
import { MuiMultiSelect, useLocale } from 'pe-core-frontend';
import { useCallback, useEffect, useState } from 'react';
export const SchoolsPicker = ({ countryCodes, className, label, schoolIds, errors, onChange, disabled, }) => {
    const { t } = useLocale();
    const [searchText, setSearchText] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const { schools, total, loading, page, setPage, pageSize } = useSchools({
        countryCodes,
        searchText,
        initialPageSize: 1000,
    });
    // Calculate total pages
    const totalPages = Math.ceil(total / pageSize);
    // Accumulate options as pages are fetched, reset on new search
    useEffect(() => {
        if (page === 1) {
            // For page 1, replace all options (new search or initial load)
            setOptions(schools.map(s => ({ label: s.name, value: s.id })));
        }
        else {
            // For subsequent pages, append new schools and deduplicate
            setOptions(prev => {
                const newSchools = schools.map(s => ({ label: s.name, value: s.id }));
                const combined = [...prev, ...newSchools];
                // Deduplicate by value
                return combined.filter((opt, idx, arr) => arr.findIndex(o => o.value === opt.value) === idx);
            });
        }
    }, [schools, page]);
    useEffect(() => {
        if (paginationLoading) {
            setPaginationLoading(false);
        }
    }, [schools, page]);
    const handleChange = (e) => {
        onChange(e.selection.map(s => s.value));
    };
    const throttleSetSearchText = useCallback(debounce((value) => setSearchText(value), 300), []);
    const handleSearch = (value) => {
        setInputValue(value); // Update the input display immediately
        throttleSetSearchText(value); // This will trigger the useSchools hook - after 300ms of no input
        if (page !== 1) {
            setPage(1);
        }
    };
    const handleScrollEnd = () => {
        if (page < totalPages && !loading) {
            setPaginationLoading(true);
            setPage(page + 1);
        }
    };
    return (_jsx(MuiMultiSelect, { className: className, label: label || t('Schools'), options: options, value: schoolIds, onChange: handleChange, errors: errors, disabled: disabled, onSearch: handleSearch, onScrollEnd: handleScrollEnd, loading: loading, inputValue: inputValue, paginationLoading: paginationLoading }));
};
